import React from 'react';
import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

// Authentication
import { AuthProvider } from './contexts/AuthContext';
import { BottomNavProvider } from './contexts/BottomNavContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import OptionalAuthRoute from './components/auth/OptionalAuthRoute';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import ForgotPasswordForm from './components/auth/ForgotPasswordForm';

// Pages
import HomePage from './pages/HomePage';
import WelcomePage from './pages/WelcomePage';
import ProjectsPage from './pages/ProjectsPage';
import NewProjectPage from './pages/NewProjectPage';
import WaitingRoomPage from './pages/WaitingRoomPage';
import UserProfile from './pages/UserProfile';
import PublicUserProfile from './pages/PublicUserProfile';
import NotFound from './pages/NotFound';
import ChatPage from './pages/ChatPage';
import ProjectRouter from './components/ProjectRouter';
import CommunityPage from './pages/CommunityPage';

// Components
import Layout from './components/Layout';
import AnimatedRoutes from './components/AnimatedRoutes';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import OfflineIndicator from './components/OfflineIndicator';

// Styles
import './index.css';
import SavedPage from './pages/SavedPage';
import FinishedProjectImagesPage from './pages/FinishedProjectImagesPage';
import ThingsExplorer from './thingsdb/pages/ThingsExplorer';
import { loadBranding } from './services/branding';
import { useBrandingStore } from './stores/brandingStore';
import AdminLayout from './components/admin/AdminLayout';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminDashboardPage from './pages/AdminDashboardPage';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

function App() {
  const setBranding = useBrandingStore((s) => s.setBranding);
  useEffect(() => {
    (async () => {
      const data = await loadBranding();
      if (data) setBranding(data);
    })();
  }, [setBranding]);
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <BottomNavProvider>
              <Router>
            <ScrollToTop />
            <OfflineIndicator />
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginForm />} />
              <Route path="/register" element={<RegisterForm />} />
              <Route path="/forgot" element={<ForgotPasswordForm />} />
              
              {/* Home page - default for logged-in users */}
              <Route path="/" element={
                <OptionalAuthRoute>
                  <Layout>
                    <HomePage />
                  </Layout>
                </OptionalAuthRoute>
              } />
              
              {/* Protected routes */}
              
              {/* Research-focused routes */}
              <Route path="/projects" element={
                <ProtectedRoute>
                  <AnimatedRoutes />
                </ProtectedRoute>
              } />
              <Route path="/new" element={
                <ProtectedRoute>
                  <Layout>
                    <NewProjectPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* IMPORTANT: More specific routes MUST come before general :projectId route */}
              <Route path="/projects/:id/waiting" element={
                <ProtectedRoute>
                  <Layout>
                    <WaitingRoomPage />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/projects/:projectId/images" element={
                <ProtectedRoute>
                  <Layout>
                    <FinishedProjectImagesPage />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* General project route - must be LAST among /projects/* routes */}
              <Route path="/projects/:projectId" element={
                <ProtectedRoute>
                  <AnimatedRoutes />
                </ProtectedRoute>
              } />
              
              <Route path="/projects/:projectId/chats/:chatId" element={
                <ProtectedRoute>
                  <Layout>
                    <ChatPage />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/profile" element={
                <ProtectedRoute>
                  <Layout>
                    <UserProfile />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Public user profile route */}
              <Route path="/users/:username" element={
                <OptionalAuthRoute>
                  <Layout>
                    <PublicUserProfile />
                  </Layout>
                </OptionalAuthRoute>
              } />
              
              <Route path="/starters" element={
                <ProtectedRoute>
                  <AnimatedRoutes />
                </ProtectedRoute>
              } />
              
              <Route path="/starters/:projectId" element={
                <ProtectedRoute>
                  <AnimatedRoutes />
                </ProtectedRoute>
              } />

              <Route path="/starters/:projectId/images" element={
                <ProtectedRoute>
                  <AnimatedRoutes />
                </ProtectedRoute>
              } />
              
              {/* Discover page */}
              <Route path="/discover" element={
                <ProtectedRoute>
                  <AnimatedRoutes />
                </ProtectedRoute>
              } />
              <Route path="/discover/:detectionId" element={
                <ProtectedRoute>
                  <AnimatedRoutes />
                </ProtectedRoute>
              } />

              {/* ThingsDB explorer (feature-gated server-side) */}
              <Route path="/things" element={
                <ProtectedRoute>
                  <Layout>
                    <ThingsExplorer />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Admin routes (superuser-only) */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminLayout>
                      <AdminDashboardPage />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <AdminLayout>
                      <AdminUsersPage />
                    </AdminLayout>
                  </AdminRoute>
                }
              />
              
              {/* Saved pages with animations */}
              <Route path="/saved" element={
                <ProtectedRoute>
                   <Layout>
                    <SavedPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/saved/:listId" element={
                <ProtectedRoute>
                  <AnimatedRoutes />
                </ProtectedRoute>
              } />
              
              {/* Welcome page - Introduction for new users */}
              <Route path="/welcome" element={
                <ProtectedRoute>
                  <Layout>
                    <WelcomePage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* 404 page */}
              <Route path="*" element={<NotFound />} />
            </Routes>
                </Router>
              </BottomNavProvider>
            </AuthProvider>
          </QueryClientProvider>
        </ThemeProvider>
        
        {/* React Query DevTools - Only in development */}
        {/* {process.env.NODE_ENV === 'development' && (
          <React.Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
          </React.Suspense>
        )} */}
        
        {/* Toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
              fontFamily: 'Inter, sans-serif',
            },
            success: {
              iconTheme: {
                primary: '#10B981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#EF4444',
                secondary: '#fff',
              },
            },
          }}
        />
    </ErrorBoundary>
  );
}

export default App; 